import { useReadCompanyServiceCode } from '@/api/accounting-dealer'
import {
  Stack,
  TextField,
  MenuItem,
  InputAdornment,
  CircularProgress,
} from '@mui/material'
import { Controller } from 'react-hook-form'
import { useFormContext } from 'react-hook-form'
import { FiscalNoteFormData } from '../../schema'
import { useEffect } from 'react'
import { MaskedInput } from '@/components'

type ServiceCodeProps = {
  companyKey: string
}

export function ServiceCode({ companyKey }: ServiceCodeProps) {
  const { control, setValue, watch } = useFormContext<FiscalNoteFormData>()

  const serviceCodeId = watch('service_code_id')

  const { data: serviceCodes = [], isLoading } =
    useReadCompanyServiceCode(companyKey)

  useEffect(() => {
    const serviceCode = serviceCodes.find(
      ({ service_code_id }) => service_code_id === Number(serviceCodeId),
    )

    if (serviceCode) {
      setValue(
        'service_tax_rate',
        (serviceCode.service_tax_rate * 100).toString(),
      )
      if (serviceCode.service_tax_code) {
        setValue(
          'service_tax_code',
          serviceCode.service_tax_code
            ? serviceCode.service_tax_code
            : serviceCode.tax_code,
        )
      }
    }
  }, [serviceCodeId, serviceCodes])

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
      <Controller
        control={control}
        name="service_code_id"
        render={({ field, fieldState: { error } }) => (
          <TextField
            select
            label="Código de serviço"
            defaultValue=""
            disabled={isLoading}
            error={!!error}
            helperText={error?.message}
            slotProps={{
              input: {
                endAdornment: isLoading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : undefined,
              },
              select: {
                IconComponent: isLoading ? () => null : undefined,
                MenuProps: {
                  sx: {
                    '& .MuiMenuItem-root': {
                      whiteSpace: 'normal',
                    },
                  },
                },
              },
            }}
            data-testid="service-code-id"
            {...field}
          >
            {serviceCodes.map(({ code, label, service_code_id }) => (
              <MenuItem
                key={service_code_id}
                value={service_code_id.toString()}
              >
                {code} - {label}
              </MenuItem>
            ))}
          </TextField>
        )}
      />

      <Controller
        control={control}
        name="service_tax_rate"
        render={({ field }) => (
          <MaskedInput
            label="Alíquota"
            format="percentage"
            sx={{ width: { xs: 'auto', sm: '30%' } }}
            disabled
            data-testid="service-tax-rate"
            {...field}
          />
        )}
      />
    </Stack>
  )
}
